// Main component for building surveys
// Manages the survey editing interface and handles saving functionality
import React, { useState, useEffect } from 'react';
import QuestionDesigner from './QuestionDesigner';
import SurveyPreview from './SurveyPreview';
import { useAuth } from '../contexts/AuthContext';
import { saveSurvey, deleteSurvey, get } from '../utils/serverComm';
import { toast } from 'react-toastify';
import { Save, BarChart, Trash2, Pencil, ChevronRight, X, Eye } from 'lucide-react';
import './SurveyBuilderForm.css';
import PropTypes from 'prop-types';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUnsavedChangesPrompt } from '../hooks/useUnsavedChangesPrompt';

function SurveyBuilderForm({ surveyId, onCancel }) {
  const [survey, setSurvey] = useState(null);
  const [localTitle, setLocalTitle] = useState('');
  const [localQuestions, setLocalQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTitleEdit, setShowTitleEdit] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const navigate = useNavigate();

  const hasUnsavedChanges = 
    localTitle !== originalTitle || 
    JSON.stringify(localQuestions) !== JSON.stringify(originalQuestions);

  useUnsavedChangesPrompt(hasUnsavedChanges);

  useEffect(() => {
    const loadSurvey = async () => {
      if (!surveyId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await get(`/api/surveys/${surveyId}`);
        const surveyData = await response.json();
        setSurvey(surveyData);
        setLocalTitle(surveyData.title || '');
        setLocalQuestions(surveyData.questions || []);
        setOriginalTitle(surveyData.title || '');
        setOriginalQuestions(surveyData.questions || []);
        setEditedTitle(surveyData.title || '');
        setError(null);
      } catch (err) {
        console.error('Failed to load survey:', err);
        setError('Failed to load survey');
        toast.error('Failed to load survey');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurvey();
  }, [surveyId]);

  const handleSaveSurvey = async () => {
    try {
      setIsSaving(true);
      const surveyData = {
        title: localTitle,
        questions: localQuestions,
      };
      
      await saveSurvey(surveyId, surveyData);
      
      setOriginalTitle(localTitle);
      setOriginalQuestions(localQuestions);
      
      toast.success('Survey saved successfully!');
    } catch (error) {
      console.error('Save error details:', {
        error,
        surveyData: { title: localTitle, questions: localQuestions },
        surveyId
      });
      toast.error('Failed to save survey');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddQuestion = (newQuestion) => {
    setLocalQuestions(prev => [...prev, newQuestion]);
  };

  const handleUpdateTitle = (newTitle) => {
    setLocalTitle(newTitle);
  };

  const handleDeleteSurvey = async () => {
    try {
      await deleteSurvey(surveyId);
      setShowDeleteConfirm(false);
      toast.success('Survey deleted successfully');
      // Navigate back to home
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete survey:', error);
      toast.error('Failed to delete survey');
    }
  };

  const handleTitleEdit = () => {
    setShowTitleEdit(true);
  };

  const handleTitleSave = () => {
    setLocalTitle(editedTitle);
    setShowTitleEdit(false);
  };

  const handleAnalyzeClick = () => {
    navigate(`/surveys/analytics/${surveyId}`);
  };

  if (isLoading) {
    return <div>Loading survey...</div>;
  }

  if (error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error}</p>
        <button onClick={onCancel}>Back to Home</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb Navigation */}
      <div className="p-4 border-b">
        <div className="flex items-center text-sm">
          <Link to="/" className="text-blue-500 hover:text-blue-600">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
          <span className="text-gray-700">Survey Builder</span>
        </div>
      </div>

      {/* Title Section */}
      <div className="p-4 flex items-center gap-2">
        <h1 className="text-3xl font-bold text-gray-900">{localTitle}</h1>
        <button
          onClick={handleTitleEdit}
          className="p-1 hover:bg-gray-100 rounded-full"
          title="Edit Title"
        >
          <Pencil className="w-5 h-5 text-gray-500" />
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="p-1 hover:bg-gray-100 rounded-full"
          title="Delete Survey"
        >
          <Trash2 className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Main content - existing two-column layout */}
      <div className="flex flex-1">
        {/* Left panel - Question Designer */}
        <div className="w-1/3 min-w-[400px] p-4">
          <QuestionDesigner onAddQuestion={handleAddQuestion} />
          <div className="space-y-2 mt-4">
            <button
              onClick={handleSaveSurvey}
              disabled={isSaving}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Survey'}
            </button>
            
            <button 
              onClick={() => window.open(`/s/${surveyId}`, '_blank')}
              className="w-full border-2 border-blue-500 text-blue-500 hover:bg-blue-50 font-bold py-2 px-4 rounded flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview Survey
            </button>
            
            <button 
              onClick={handleAnalyzeClick}
              className="w-full border-2 border-blue-500 text-blue-500 hover:bg-blue-50 font-bold py-2 px-4 rounded flex items-center justify-center gap-2"
            >
              <BarChart className="w-4 h-4" />
              Analyze Responses
            </button>
          </div>
        </div>

        {/* Right panel - Survey Preview */}
        <div className="w-2/3 p-4 overflow-y-auto">
          <SurveyPreview 
            title={localTitle}
            questions={localQuestions} 
            setQuestions={setLocalQuestions}
            onUpdateTitle={handleUpdateTitle}
          />
        </div>
      </div>

      {/* Title Edit Modal */}
      {showTitleEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Edit Survey Title</h3>
              <button
                onClick={() => setShowTitleEdit(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="w-full p-2 border rounded mb-4"
              placeholder="Enter survey title"
            />
            <div className="flex justify-end">
              <button
                onClick={handleTitleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Title
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="text-lg font-medium mb-4">Delete Survey</h3>
            <p>Are you sure you want to delete this survey? This action cannot be undone.</p>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSurvey}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

SurveyBuilderForm.propTypes = {
  surveyId: PropTypes.string,
  onCancel: PropTypes.func.isRequired
};

export default SurveyBuilderForm;